import * as stylex from "@stylexjs/stylex";
import { format } from "d3-format";
import { defineMessage, useIntl } from "react-intl";
import { withDataLoader } from "../components/data-loader";
import { EmbedChart, StaticChart } from "../components/figure";
import { HeroControlsWrapper } from "../components/hero-controls/hero-controls";
import { Area } from "../components/hero-vis/area";
import { Hero } from "../components/hero-vis/hero";
import { Lines } from "../components/hero-vis/line";
import MultipleHeroes from "../components/multiple-heroes";
import { metadata } from "../data/data_access_hero";
import { datasets } from "../data/data_access_hero_datasets";
import { mkGemEntityDecoder, useCountryEntities, useNamedEntities, useRegionEntities } from "../domain";
import { foldChartType, HeroStateProvider, useChartType } from "../domain/hero-state";
import { useContentRect } from "../hooks";
import { layout } from "../materials/layout.stylex";
import { io } from "../prelude";
export * from "../data/data_access_hero";
const formatValue = format(".0%");
const formatAxisValue = format(".0%");
const targetTitle = defineMessage({
    id: "fig.access.HERO.targetTitle",
    defaultMessage: "2030 Target"
});
const targetLabel = defineMessage({
    id: "fig.access.HERO.target",
    defaultMessage: "100% of children complete upper secondary school"
});
const infoLabel = defineMessage({
    id: "fig.access.HERO.info",
    defaultMessage: "100% of children complete upper secondary school"
});
export const Data = mkGemEntityDecoder([
    "ind_id",
    "year"
], {
    value: io.number
});
export const Chart = ({ data: dataRaw, width, csv, xlsx, renderMode, dataset })=>{
    const intl = useIntl();
    const [chartType] = useChartType();
    const indicatorLabel = intl.formatMessage(defineMessage({
        id: "fig.access.HERO.indicatorLabel",
        defaultMessage: "Complete primary school"
    }));
    const tooltipRowLabels = [
        intl.formatMessage(defineMessage({
            id: "fig.access.HERO.tooltipNotReached",
            defaultMessage: "Not completed"
        })),
        intl.formatMessage(defineMessage({
            id: "fig.access.HERO.tooltipReached",
            defaultMessage: "Completed"
        }))
    ];
    const data = useNamedEntities(dataRaw);
    const countryData = useCountryEntities(data);
    const regionData = useRegionEntities(data);
    return <HeroControlsWrapper renderMode={renderMode} caption={intl.formatMessage(metadata.caption)} content={intl.formatMessage(infoLabel)} source={intl.formatMessage(metadata.source)} id={metadata.id} csv={csv} xlsx={xlsx} dataset={dataset}>
      {foldChartType(chartType, {
        today: ()=><Area key="today" label={indicatorLabel} width={width} values={countryData} tooltipRowLabels={tooltipRowLabels} formatValue={formatValue} formatAxisValue={formatAxisValue}/>,
        timeseries: ()=><Lines key="timeseries" label={indicatorLabel} width={width} values={regionData} staggerDelay={renderMode === "static" ? 0 : undefined} showTarget={renderMode !== "embed"}/>
    })}
    </HeroControlsWrapper>;
};
const csv = new URL("../data/data_access_hero.zip", import.meta.url).href;
const xlsx = new URL("../data/data_access_hero.xlsx", import.meta.url).href;
const HA = ({ renderMode = "default", dataset, renderMultiple })=>{
    const intl = useIntl();
    const [ref, contentRect] = useContentRect();
    const Renderer = withDataLoader(dataset ? datasets[dataset].url : new URL("../data/data_access_hero.json", import.meta.url).href, Data, (props)=>contentRect.width === 0 ? null : dataset ? <Chart {...props} width={contentRect.width} csv={datasets[dataset].csv} xlsx={datasets[dataset].xlsx} dataset={dataset}/> : <Chart {...props} width={contentRect.width} csv={csv} xlsx={xlsx} dataset={dataset}/>, {
        narrow: 400,
        wide: 400
    });
    const children = <div ref={ref} {...stylex.props(layout.fullWidthHidden)}>
      <Renderer key="access" renderMode={renderMode}/>
    </div>;
    return renderMode === "embed" ? <EmbedChart caption={intl.formatMessage(infoLabel)} source={intl.formatMessage(metadata.source)} isHeroChart>
      <HeroStateProvider initial={"today"}>{children}</HeroStateProvider>
    </EmbedChart> : renderMode === "static" ? <StaticChart layout={"columns"} title={metadata.title ? intl.formatMessage(metadata.title) : undefined} caption={intl.formatMessage(metadata.caption)} source={intl.formatMessage(metadata.source)}>
      <HeroStateProvider initial={"today"}>{children}</HeroStateProvider>
      <div {...stylex.props(layout.heroSpacer)}/>
      <HeroStateProvider initial={"timeseries"}>{children}</HeroStateProvider>
    </StaticChart> : renderMultiple ? <MultipleHeroes datasets={datasets} Component={HA}/> : <HeroStateProvider>
      <Hero title={metadata.title ? intl.formatMessage(metadata.title) : undefined} caption={intl.formatMessage(metadata.caption)} targetTitle={intl.formatMessage(targetTitle)} targetLabel={intl.formatMessage(targetLabel)}>
        {children}
      </Hero>
    </HeroStateProvider>;
};
export default HA;
